import { useState } from 'react';
import { submitApply, uploadProofFile } from '../../../api/dripPlan';

interface FormData {
  name: string;
  phone: string;
  workUnit: string;
  region: string;
  reason: string;
  files: File[];
}

const ApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    workUnit: '',
    region: '',
    reason: '',
    files: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const regions = [
    '渝中区', '江北区', '南岸区', '九龙坡区', '沙坪坝区', '大渡口区',
    '渝北区', '巴南区', '北碚区', '綦江区', '长寿区', '江津区',
    '合川区', '永川区', '南川区', '璧山区', '铜梁区', '潼南区',
    '荣昌区', '开州区', '梁平区', '武隆区', '城口县', '丰都县',
    '垫江县', '忠县', '云阳县', '奉节县', '巫山县', '巫溪县',
    '石柱县', '秀山县', '酉阳县', '彭水县'
  ];

  const steps = [
    { number: 1, title: '基本信息', icon: 'ri-user-line' },
    { number: 2, title: '工作信息', icon: 'ri-briefcase-line' },
    { number: 3, title: '证明材料', icon: 'ri-file-upload-line' },
    { number: 4, title: '提交确认', icon: 'ri-checkbox-circle-line' }
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (formData.files.length + files.length > 5) {
      alert('最多只能上传5个文件');
      return;
    }
    setFormData({ ...formData, files: [...formData.files, ...files] });
  };

  const removeFile = (index: number) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData({ ...formData, files: newFiles });
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return formData.name.trim() !== '' && formData.phone.trim() !== '' && /^1[3-9]\d{9}$/.test(formData.phone);
      case 2:
        return formData.workUnit.trim() !== '' && formData.region !== '';
      case 3:
        return formData.reason.trim() !== '' && formData.reason.length <= 200 && formData.files.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    try {
      const proofFileNames: string[] = [];
      for (const file of formData.files) {
        const filename = await uploadProofFile(file);
        proofFileNames.push(filename);
      }
      const { applicationNumber: num } = await submitApply({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        workUnit: formData.workUnit.trim(),
        region: formData.region,
        reason: formData.reason.trim(),
        proofCount: proofFileNames.length,
        proofFileNames,
      });
      setApplicationNumber(num);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 md:py-12">
        <div className="w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 animate-[bounce_1s_ease-in-out]">
          <i className="ri-checkbox-circle-fill text-white text-3xl md:text-5xl"></i>
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#1E1E1E] mb-3 md:mb-4">申请提交成功！</h3>
        <p className="text-base md:text-lg text-[#6C6C6C] mb-4 md:mb-6">您的申请编号为：</p>
        <div className="inline-block bg-[#F9F9F9] px-6 py-3 md:px-8 md:py-4 rounded-xl mb-6 md:mb-8">
          <span className="text-2xl md:text-3xl font-bold text-[#2A7A62]">{applicationNumber}</span>
        </div>
        <div className="text-sm md:text-base text-[#6C6C6C] mb-6 md:mb-8 max-w-xl mx-auto px-4 space-y-2">
          <p>请保存好您的申请编号，</p>
          <p>我们将在3-5个工作日内完成审核。</p>
          <p className="mt-3">您可使用本手机号在「查询申请状态」页面查看审核进度。</p>
        </div>
        <button
          onClick={() => {
            setSubmitted(false);
            setCurrentStep(1);
            setFormData({
              name: '',
              phone: '',
              workUnit: '',
              region: '',
              reason: '',
              files: []
            });
          }}
          className="bg-gradient-to-r from-[#2A7A62] to-[#3A9A82] text-white px-6 py-2.5 md:px-8 md:py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base"
        >
          提交新申请
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Step Indicator */}
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                    currentStep >= step.number
                      ? 'bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] text-white shadow-lg scale-110'
                      : 'bg-[#E0E0E0] text-[#6C6C6C]'
                  }`}
                >
                  <i className={`${step.icon} text-lg md:text-2xl`}></i>
                </div>
                <span
                  className={`mt-2 md:mt-3 text-xs md:text-sm font-medium ${
                    currentStep >= step.number ? 'text-[#2A7A62]' : 'text-[#6C6C6C]'
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 md:h-1 flex-1 mx-2 md:mx-4 transition-all duration-300 ${
                    currentStep > step.number ? 'bg-[#2A7A62]' : 'bg-[#E0E0E0]'
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Basic Information */}
      {currentStep === 1 && (
        <div className="space-y-4 md:space-y-6 animate-[fadeIn_0.3s_ease-out]">
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              申请人姓名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full h-10 md:h-12 px-3 md:px-4 border border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base"
              placeholder="请输入您的真实姓名"
              required
            />
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              联系电话 <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full h-10 md:h-12 px-3 md:px-4 border border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base"
              placeholder="请输入11位手机号码"
              pattern="^1[3-9]\d{9}$"
              required
            />
            {formData.phone && !/^1[3-9]\d{9}$/.test(formData.phone) && (
              <p className="text-red-500 text-xs md:text-sm mt-1">请输入正确的手机号码</p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Work Information */}
      {currentStep === 2 && (
        <div className="space-y-4 md:space-y-6 animate-[fadeIn_0.3s_ease-out]">
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              工作单位名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="workUnit"
              value={formData.workUnit}
              onChange={(e) => setFormData({ ...formData, workUnit: e.target.value })}
              className="w-full h-10 md:h-12 px-3 md:px-4 border border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base"
              placeholder="请输入您的工作单位全称"
              required
            />
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              所属区域 <span className="text-red-500">*</span>
            </label>
            <select
              name="region"
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              className="w-full h-10 md:h-12 px-3 md:px-4 border border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base cursor-pointer"
              required
            >
              <option value="">请选择所属区域</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Step 3: Supporting Documents */}
      {currentStep === 3 && (
        <div className="space-y-4 md:space-y-6 animate-[fadeIn_0.3s_ease-out]">
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              申请理由 <span className="text-red-500">*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full h-28 md:h-32 px-3 md:px-4 py-2 md:py-3 border border-[#E0E0E0] rounded-lg focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base resize-none"
              placeholder="请简要说明申请理由（200字以内）"
              maxLength={200}
              required
            ></textarea>
            <div className="text-right text-xs md:text-sm text-[#6C6C6C] mt-1">
              {formData.reason.length}/200
            </div>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-[#1E1E1E] mb-2">
              证明材料 <span className="text-red-500">*</span>
            </label>
            <p className="text-xs md:text-sm text-[#6C6C6C] mb-3">
              请上传工作证、工作照片或单位证明等材料（支持图片/PDF，最多5个文件）
            </p>
            <div className="border-2 border-dashed border-[#E0E0E0] rounded-lg p-6 md:p-8 text-center hover:border-[#2A7A62] transition-colors">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex flex-col items-center"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#F9F9F9] rounded-full flex items-center justify-center mb-3 md:mb-4">
                  <i className="ri-upload-cloud-2-line text-[#2A7A62] text-2xl md:text-3xl"></i>
                </div>
                <span className="text-sm md:text-base text-[#2A7A62] font-medium">点击上传文件</span>
                <span className="text-xs md:text-sm text-[#6C6C6C] mt-2">或拖拽文件到此处</span>
              </label>
            </div>
            {formData.files.length > 0 && (
              <div className="mt-3 md:mt-4 space-y-2">
                {formData.files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#F9F9F9] px-3 md:px-4 py-2 md:py-3 rounded-lg"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <i className="ri-file-line text-[#2A7A62] text-lg md:text-xl"></i>
                      <span className="text-xs md:text-sm text-[#1E1E1E] truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      <i className="ri-close-line text-lg md:text-xl"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {currentStep === 4 && (
        <div className="space-y-4 md:space-y-6 animate-[fadeIn_0.3s_ease-out]">
          <div className="bg-[#F9F9F9] rounded-xl p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-bold text-[#1E1E1E] mb-4 md:mb-6">请确认您的申请信息</h3>
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">申请人：</span>
                <span className="text-[#1E1E1E] font-medium text-sm md:text-base">{formData.name}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">联系电话：</span>
                <span className="text-[#1E1E1E] font-medium text-sm md:text-base">{formData.phone}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">工作单位：</span>
                <span className="text-[#1E1E1E] font-medium text-sm md:text-base">{formData.workUnit}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">所属区域：</span>
                <span className="text-[#1E1E1E] font-medium text-sm md:text-base">{formData.region}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">申请理由：</span>
                <span className="text-[#1E1E1E] text-sm md:text-base">{formData.reason}</span>
              </div>
              <div className="flex items-start gap-3 md:gap-4">
                <span className="text-[#6C6C6C] w-20 md:w-24 flex-shrink-0 text-sm md:text-base">证明材料：</span>
                <span className="text-[#1E1E1E] text-sm md:text-base">{formData.files.length} 个文件</span>
              </div>
            </div>
          </div>
          <div className="bg-[#FFF9E6] border border-[#F4B942] rounded-lg p-3 md:p-4">
            <p className="text-xs md:text-sm text-[#1E1E1E]">
              <i className="ri-information-line text-[#F4B942] mr-2"></i>
              提交后我们将在3-5个工作日内完成审核，请保持电话畅通。审核结果将通过短信或电话通知您。
            </p>
          </div>
        </div>
      )}

      {submitError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
          {submitError}
        </div>
      )}
      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 md:mt-12 pt-6 md:pt-8 border-t border-[#E0E0E0]">
        {currentStep > 1 && (
          <button
            type="button"
            onClick={() => setCurrentStep(currentStep - 1)}
            disabled={submitting}
            className="px-6 py-2.5 md:px-8 md:py-3 border border-[#2A7A62] text-[#2A7A62] rounded-lg font-medium hover:bg-[#2A7A62]/5 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base disabled:opacity-50"
          >
            上一步
          </button>
        )}
        {currentStep < 4 ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={!isStepValid(currentStep)}
            className={`ml-auto px-6 py-2.5 md:px-8 md:py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base ${
              isStepValid(currentStep)
                ? 'bg-gradient-to-r from-[#2A7A62] to-[#3A9A82] text-white hover:shadow-lg'
                : 'bg-[#E0E0E0] text-[#6C6C6C] cursor-not-allowed'
            }`}
          >
            下一步
          </button>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            className="ml-auto px-6 py-2.5 md:px-8 md:py-3 bg-gradient-to-r from-[#2A7A62] to-[#3A9A82] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base disabled:opacity-50"
          >
            {submitting ? '提交中…' : '提交申请'}
          </button>
        )}
      </div>
    </form>
  );
};

export default ApplicationForm;
